import React, { useState, useEffect, useCallback } from 'react';
import { useNavigate } from 'react-router-dom';
import {
  Card, Table, Button, Modal, Form, Input, Select, DatePicker, InputNumber,
  Space, Tag, Tabs, Row, Col, Statistic, message, Popconfirm, Typography,
  Progress, Avatar, List, Descriptions, Spin, Tooltip, Empty, Divider,
} from 'antd';
import {
  TrophyOutlined, PlusOutlined, PlayCircleOutlined, DeleteOutlined,
  EditOutlined, RobotOutlined, ReloadOutlined, ThunderboltOutlined, MessageOutlined,
  CrownOutlined, MailOutlined, FacebookOutlined, FileTextOutlined,
  DollarOutlined, RiseOutlined, TeamOutlined, BarChartOutlined,
  GlobalOutlined, VideoCameraOutlined, GoogleOutlined, ReadOutlined,
} from '@ant-design/icons';
import { aiSalesAPI } from '../services/api';
import AlayaessAgentPicker, { AlayaessAgentPickerHint } from '../components/AlayaessAgentPicker';
import dayjs from 'dayjs';

const { Title, Text } = Typography;
const { TextArea } = Input;

const STATUS_COLORS = {
  draft: 'default', active: 'green', paused: 'orange',
  completed: 'blue', cancelled: 'red',
};

const COUNTRY_OPTIONS = [
  { code: 'TW', label: '台灣', flag: '🇹🇼' },
  { code: 'TH', label: '泰國', flag: '🇹🇭' },
  { code: 'VN', label: '越南', flag: '🇻🇳' },
  { code: 'AU', label: '澳洲', flag: '🇦🇺' },
];
const countryLabel = (code) => {
  const c = COUNTRY_OPTIONS.find(o => o.code === code);
  return c ? `${c.flag} ${c.label}` : (code || '—');
};

const PERIOD_OPTIONS = [
  { value: 'week', label: '本周' },
  { value: 'month', label: '本月' },
  { value: 'quarter', label: '本季' },
  { value: 'all', label: '累計' },
];

// Inline usage badge under each token-limit input in the Edit Bot modal.
// Reads the displayed bot's current usage from the bots list; falls back to
// "—" when creating a new bot (no usage yet).
function BotQuotaUsage({ form, editingBotId, bots, kind }) {
  if (!editingBotId) return <Text type="secondary" style={{ fontSize: 12 }}>目前已用：—</Text>;
  const bot = (bots || []).find(b => b.id === editingBotId);
  if (!bot) return null;
  const used = bot[`${kind}_tokens_used`] || 0;
  const limit = form.getFieldValue(`${kind}_token_limit`);
  if (!limit) {
    return <Text type="secondary" style={{ fontSize: 12 }}>目前已用：{used.toLocaleString()}（無上限）</Text>;
  }
  const pct = Math.min(100, Math.round((used / limit) * 100));
  const status = pct >= 100 ? 'exception' : pct >= 80 ? 'active' : 'normal';
  const color = pct >= 100 ? '#dc2626' : pct >= 80 ? '#d97706' : '#16a34a';
  return (
    <div style={{ marginTop: -8, marginBottom: 12 }}>
      <Progress percent={pct} size="small" status={status} strokeColor={color} showInfo={false} />
      <Text type="secondary" style={{ fontSize: 12 }}>
        {used.toLocaleString()} / {limit.toLocaleString()} ({pct}%)
        {pct >= 100 ? ' — 已封頂' : pct >= 80 ? ' — 接近上限' : ''}
      </Text>
    </div>
  );
}

const CHANNEL_KEY_LABELS = {
  email_blast: 'Email',
  fb_post: 'Facebook',
  blog_post: 'Blog',
  video_generate: 'Video',
  video: 'Video',
  google_ads_create: 'Google Ads',
  google_ads: 'Google Ads',
  generate_content: 'Content',
  sale: 'Sale',
  inquiry: 'Inquiry',
};
const TASK_KIND_LABEL = {
  blog_post: '✍ Writing blog post',
  video: '🎬 Generating AI video',
  fb_post: '📘 Posting to Facebook',
  google_ads: '💰 Drafting Google Ads',
  email_blast: '📧 Sending email blast',
  email_campaign: '📧 Email campaign',
  generate_content: '📝 Generating content',
};
const METRIC_OPTIONS = [
  { value: 'revenue', label: '成交金額' },
  { value: 'conversions', label: '成交客戶數' },
];

const RANK_COLORS = ['#faad14', '#a0a0a0', '#d48806'];
const RANK_ICONS = [
  <CrownOutlined style={{ color: '#faad14', fontSize: 18 }} />,
  <CrownOutlined style={{ color: '#a0a0a0', fontSize: 16 }} />,
  <CrownOutlined style={{ color: '#d48806', fontSize: 14 }} />,
];

export default function AISalesCompetitionPage() {
  const navigate = useNavigate();
  const [dashboard, setDashboard] = useState({});
  const [leaderboard, setLeaderboard] = useState([]);
  const [competitions, setCompetitions] = useState([]);
  const [bots, setBots] = useState([]);
  const [loading, setLoading] = useState(false);
  const [compModal, setCompModal] = useState(false);
  const [botModal, setBotModal] = useState(false);
  const [editingCompId, setEditingCompId] = useState(null);
  const [editingBotId, setEditingBotId] = useState(null);
  const [activitiesModal, setActivitiesModal] = useState(null);
  const [activities, setActivities] = useState([]);
  const [actionModal, setActionModal] = useState(null);
  const [activeTab, setActiveTab] = useState('leaderboard');
  const [lbPeriod, setLbPeriod] = useState('month');
  const [lbMetric, setLbMetric] = useState('revenue');
  const [lbCountry, setLbCountry] = useState(undefined);
  const [liveOps, setLiveOps] = useState(null);
  const [liveOpsPeriod, setLiveOpsPeriod] = useState('month');
  const [compForm] = Form.useForm();
  const [botForm] = Form.useForm();

  const fetchLeaderboard = useCallback(async () => {
    try {
      const params = { period: lbPeriod, metric: lbMetric };
      if (lbCountry) params.country = lbCountry;
      const res = await aiSalesAPI.leaderboard(params);
      setLeaderboard(res.data || []);
    } catch { /* ignore */ }
  }, [lbPeriod, lbMetric, lbCountry]);

  const fetchLiveOps = useCallback(async () => {
    try {
      const res = await aiSalesAPI.liveOperations({ period: liveOpsPeriod });
      setLiveOps(res.data || null);
    } catch { /* ignore */ }
  }, [liveOpsPeriod]);

  useEffect(() => { fetchLiveOps(); }, [fetchLiveOps]);
  // Auto-refresh Live Ops every 30s while tab is active
  useEffect(() => {
    if (activeTab !== 'liveops') return;
    const t = setInterval(fetchLiveOps, 30000);
    return () => clearInterval(t);
  }, [activeTab, fetchLiveOps]);

  const fetchAll = useCallback(async () => {
    setLoading(true);
    try {
      const [dashRes, compRes, botRes] = await Promise.all([
        aiSalesAPI.dashboard(),
        aiSalesAPI.listCompetitions(),
        aiSalesAPI.listBots(),
      ]);
      setDashboard(dashRes.data || {});
      setCompetitions(compRes.data || []);
      setBots(botRes.data || []);
    } catch { /* ignore */ }
    await fetchLeaderboard();
    setLoading(false);
  }, [fetchLeaderboard]);

  useEffect(() => { fetchAll(); }, [fetchAll]);
  useEffect(() => { fetchLeaderboard(); }, [fetchLeaderboard]);

  // Competition CRUD
  const handleSaveComp = async () => {
    try {
      const values = await compForm.validateFields();
      if (values.start_date) values.start_date = values.start_date.format('YYYY-MM-DD');
      if (values.end_date) values.end_date = values.end_date.format('YYYY-MM-DD');
      if (editingCompId) {
        await aiSalesAPI.updateCompetition(editingCompId, values);
        message.success('Competition updated');
      } else {
        await aiSalesAPI.createCompetition(values);
        message.success('Competition created');
      }
      setCompModal(false);
      compForm.resetFields();
      setEditingCompId(null);
      fetchAll();
    } catch (e) { if (e.message) message.error(e.message); }
  };

  const handleEditComp = (record) => {
    setEditingCompId(record.id);
    const vals = { ...record };
    if (vals.start_date) vals.start_date = dayjs(vals.start_date);
    if (vals.end_date) vals.end_date = dayjs(vals.end_date);
    compForm.setFieldsValue(vals);
    setCompModal(true);
  };

  const handleDeleteComp = async (id) => {
    await aiSalesAPI.deleteCompetition(id);
    message.success('Deleted');
    fetchAll();
  };

  // Bot CRUD
  const handleSaveBot = async () => {
    try {
      const values = await botForm.validateFields();
      if (editingBotId) {
        await aiSalesAPI.updateBot(editingBotId, values);
        message.success('Bot updated');
      } else {
        await aiSalesAPI.createBot(values);
        message.success('Bot created');
      }
      setBotModal(false);
      botForm.resetFields();
      setEditingBotId(null);
      fetchAll();
    } catch (e) { if (e.message) message.error(e.message); }
  };

  const handleEditBot = (record) => {
    setEditingBotId(record.id);
    botForm.setFieldsValue(record);
    setBotModal(true);
  };

  const handleDeleteBot = async (id) => {
    await aiSalesAPI.deleteBot(id);
    message.success('Deleted');
    fetchAll();
  };

  const showActivities = async (botId) => {
    try {
      const res = await aiSalesAPI.botActivities(botId);
      setActivities(res.data || []);
      setActivitiesModal(botId);
    } catch { message.error('Failed to load activities'); }
  };

  const handleBotAction = async (botId, actionType) => {
    try {
      await aiSalesAPI.botAction(botId, { action_type: actionType, params: {} });
      message.success(`Action "${actionType}" started`);
      setActionModal(null);
      setTimeout(fetchAll, 3000);
    } catch { message.error('Action failed'); }
  };

  // Leaderboard columns
  const lbColumns = [
    {
      title: 'Rank', dataIndex: 'rank', width: 70, align: 'center',
      render: r => r <= 3 ? RANK_ICONS[r - 1] : <Text type="secondary">{r}</Text>,
    },
    {
      title: 'Bot', width: 220,
      render: (_, r) => (
        <Space>
          <Avatar src={r.avatar_url} icon={<RobotOutlined />}
            style={{ background: r.rank <= 3 ? RANK_COLORS[r.rank - 1] : '#1890ff' }} />
          <div>
            <Text strong>{r.bot_name}</Text>
            <div>
              <Space size={4}>
                <Tag color={STATUS_COLORS[r.status]}>{r.status}</Tag>
                {r.country_code && <Tag>{countryLabel(r.country_code)}</Tag>}
              </Space>
            </div>
          </div>
        </Space>
      ),
    },
    {
      title: '成交金額', dataIndex: 'total_revenue', width: 130, align: 'right',
      render: v => <Text strong style={{ color: '#52c41a' }}>NT${(v || 0).toLocaleString()}</Text>,
    },
    {
      title: 'Progress', width: 200,
      render: (_, r) => (
        <div>
          <Progress percent={Math.min(r.progress_pct, 100)} size="small"
            strokeColor={r.progress_pct >= 100 ? '#52c41a' : r.progress_pct >= 70 ? '#1890ff' : '#faad14'} />
          <Text type="secondary" style={{ fontSize: 11 }}>
            NT${(r.total_revenue || 0).toLocaleString()} / NT${(r.target_amount || 0).toLocaleString()}
          </Text>
        </div>
      ),
    },
    { title: 'Activities', dataIndex: 'total_activities', width: 90, align: 'center' },
    {
      title: '成交客戶數', dataIndex: 'total_conversions', width: 110, align: 'center',
      render: v => <Text strong style={{ color: '#722ed1' }}>{v}</Text>,
    },
    {
      title: 'Actions', width: 200, fixed: 'right',
      render: (_, r) => (
        <Space size="small">
          <Button size="small" icon={<ThunderboltOutlined />} type="primary"
            onClick={() => setActionModal(r.bot_id)}>Action</Button>
          <Tooltip title="Detail dashboard">
            <Button size="small" icon={<BarChartOutlined />}
              onClick={() => navigate(`/ai-sales/bots/${r.bot_id}`)} />
          </Tooltip>
        </Space>
      ),
    },
  ];

  // Competition columns
  const compColumns = [
    { title: 'Name', dataIndex: 'name', width: 200, ellipsis: true },
    {
      title: 'Period', dataIndex: 'period_type', width: 100,
      render: v => <Tag>{v}</Tag>,
    },
    {
      title: 'Duration', width: 200,
      render: (_, r) => (
        <Text type="secondary">
          {r.start_date ? dayjs(r.start_date).format('YYYY/MM/DD') : '-'}
          {' ~ '}
          {r.end_date ? dayjs(r.end_date).format('YYYY/MM/DD') : '-'}
        </Text>
      ),
    },
    {
      title: 'Target', dataIndex: 'target_amount', width: 130, align: 'right',
      render: v => v ? `NT$${v.toLocaleString()}` : '-',
    },
    {
      title: 'Status', dataIndex: 'status', width: 90,
      render: s => <Tag color={STATUS_COLORS[s]}>{s}</Tag>,
    },
    {
      title: 'Reward', dataIndex: 'reward_description', width: 200, ellipsis: true,
    },
    {
      title: 'Actions', width: 120, fixed: 'right',
      render: (_, r) => (
        <Space size="small">
          <Button size="small" icon={<EditOutlined />} onClick={() => handleEditComp(r)} />
          <Popconfirm title="Delete?" onConfirm={() => handleDeleteComp(r.id)}>
            <Button size="small" icon={<DeleteOutlined />} danger />
          </Popconfirm>
        </Space>
      ),
    },
  ];

  // Bot columns
  const botColumns = [
    {
      title: 'Bot', width: 220,
      render: (_, r) => (
        <Space>
          <Avatar src={r.avatar_url} icon={<RobotOutlined />} style={{ background: '#1890ff' }} />
          <div>
            <Text strong>{r.name}</Text>
            <div>
              <Space size={4}>
                {r.country_code && <Tag>{countryLabel(r.country_code)}</Tag>}
                {r.personality && <Text type="secondary" style={{ fontSize: 11 }}>{r.personality}</Text>}
              </Space>
            </div>
          </div>
        </Space>
      ),
    },
    {
      title: 'Target', dataIndex: 'target_amount', width: 130, align: 'right',
      render: v => v ? `NT$${v.toLocaleString()}` : '-',
    },
    {
      title: 'Revenue', dataIndex: 'total_revenue', width: 130, align: 'right',
      render: v => <Text style={{ color: '#52c41a' }}>NT${(v || 0).toLocaleString()}</Text>,
    },
    { title: 'Activities', dataIndex: 'total_activities', width: 90, align: 'center' },
    {
      title: 'Status', dataIndex: 'status', width: 90,
      render: s => <Tag color={STATUS_COLORS[s]}>{s}</Tag>,
    },
    {
      title: 'Strategy', dataIndex: 'strategy_config', width: 200, ellipsis: true,
    },
    {
      title: 'Actions', width: 240, fixed: 'right',
      render: (_, r) => (
        <Space size="small">
          <Button size="small" icon={<ThunderboltOutlined />} type="primary"
            onClick={() => setActionModal(r.id)}>Action</Button>
          <Tooltip title="跟這個 bot 對話 + 觸發動作">
            <Button size="small" icon={<MessageOutlined />}
              onClick={() => navigate(`/ai-sales/bots/${r.id}/chat`)} />
          </Tooltip>
          <Tooltip title="Detail dashboard">
            <Button size="small" icon={<BarChartOutlined />}
              onClick={() => navigate(`/ai-sales/bots/${r.id}`)} />
          </Tooltip>
          <Button size="small" icon={<EditOutlined />} onClick={() => handleEditBot(r)} />
          <Popconfirm title="Delete?" onConfirm={() => handleDeleteBot(r.id)}>
            <Button size="small" icon={<DeleteOutlined />} danger />
          </Popconfirm>
        </Space>
      ),
    },
  ];

  const d = dashboard;

  const tabItems = [
    {
      key: 'liveops',
      label: <span><ThunderboltOutlined /> Live Operations {liveOps?.live_count > 0 && <Tag color="red" style={{ marginLeft: 6 }}>🔴 {liveOps.live_count}</Tag>}</span>,
      children: (
        <>
          <Row align="middle" gutter={[12, 12]} style={{ marginBottom: 16 }}>
            <Col>
              <Text type="secondary" style={{ marginRight: 8 }}>結算期間</Text>
              <Select size="small" value={liveOpsPeriod} onChange={setLiveOpsPeriod}
                options={PERIOD_OPTIONS} style={{ width: 110 }} />
            </Col>
            <Col>
              <Statistic title="Live now" value={liveOps?.live_count ?? '–'} suffix={`/${liveOps?.total_bots ?? '–'}`}
                valueStyle={{ fontSize: 18, color: '#ff4d4f' }} />
            </Col>
            <Col>
              <Statistic title="Total tokens" value={(liveOps?.grand_tokens ?? 0).toLocaleString()}
                valueStyle={{ fontSize: 18, color: '#722ed1' }} />
            </Col>
            <Col flex="auto" style={{ textAlign: 'right' }}>
              <Text type="secondary" style={{ fontSize: 12, marginRight: 8 }}>每 30 秒自動重整</Text>
              <Button size="small" icon={<ReloadOutlined />} onClick={fetchLiveOps}>重整</Button>
            </Col>
          </Row>
          {!liveOps || liveOps.ops.length === 0 ? (
            <Empty description="尚無 bot — 先到 AI Bots 分頁建立" />
          ) : (
            <Row gutter={[16, 16]}>
              {liveOps.ops.map(o => {
                const task = o.current_task;
                const kindKey = task?.asset_type || task?.activity_type;
                const taskLabel = TASK_KIND_LABEL[kindKey] || kindKey || 'idle';
                const startedAgo = task?.started_at
                  ? Math.round((Date.now() - new Date(task.started_at).getTime()) / 60000)
                  : null;
                const maxToken = Math.max(...Object.values(o.tokens_by_channel || {}), 1);
                return (
                  <Col xs={24} sm={12} lg={8} xl={6} key={o.bot_id}>
                    <Card
                      hoverable
                      onClick={() => navigate(`/ai-sales/bots/${o.bot_id}`)}
                      style={{
                        borderRadius: 10,
                        borderColor: o.live_now ? '#ff4d4f' : '#f0f0f0',
                        boxShadow: o.live_now ? '0 0 0 1px #ff4d4f33, 0 4px 16px rgba(255,77,79,0.15)' : '0 1px 4px rgba(0,0,0,0.05)',
                      }}
                      bodyStyle={{ padding: 16 }}
                    >
                      <div style={{ display: 'flex', alignItems: 'center', gap: 12, marginBottom: 12 }}>
                        <div style={{ position: 'relative' }}>
                          <Avatar src={o.avatar_url} icon={<RobotOutlined />} size={48}
                            style={{ background: '#1890ff' }} />
                          {o.live_now && (
                            <span style={{
                              position: 'absolute', bottom: 0, right: 0,
                              width: 14, height: 14, borderRadius: '50%',
                              background: '#ff4d4f', border: '2px solid #fff',
                              animation: 'aiSalesLivePulse 1.5s ease-in-out infinite',
                            }} />
                          )}
                        </div>
                        <div style={{ flex: 1, minWidth: 0 }}>
                          <Text strong style={{ fontSize: 15, display: 'block' }}>{o.bot_name}</Text>
                          <Space size={4}>
                            {o.country_code && <Tag style={{ margin: 0 }}>{countryLabel(o.country_code)}</Tag>}
                            <Tag color={o.live_now ? 'red' : 'default'} style={{ margin: 0 }}>
                              {o.live_now ? 'LIVE' : 'idle'}
                            </Tag>
                          </Space>
                        </div>
                      </div>

                      <div style={{
                        background: o.live_now ? '#fff1f0' : '#fafafa',
                        borderLeft: `3px solid ${o.live_now ? '#ff4d4f' : '#d9d9d9'}`,
                        padding: '6px 10px', borderRadius: 4, marginBottom: 10, minHeight: 50,
                      }}>
                        <div style={{ fontSize: 12, color: '#999' }}>Currently:</div>
                        <Text style={{ fontSize: 13, fontWeight: 600 }}>{taskLabel}</Text>
                        {task?.title && (
                          <div style={{ fontSize: 11, color: '#666', marginTop: 2, whiteSpace: 'nowrap', overflow: 'hidden', textOverflow: 'ellipsis' }}>
                            "{task.title}"
                          </div>
                        )}
                        {startedAgo != null && (
                          <div style={{ fontSize: 10, color: '#999', marginTop: 2 }}>
                            {startedAgo < 1 ? '剛剛' : `${startedAgo} 分鐘前`}
                          </div>
                        )}
                      </div>

                      <div style={{ marginBottom: 10 }}>
                        <div style={{ display: 'flex', justifyContent: 'space-between', marginBottom: 4 }}>
                          <Text style={{ fontSize: 12, color: '#999' }}>Tokens used</Text>
                          <Text strong style={{ fontSize: 13, color: '#722ed1' }}>
                            {(o.tokens_total || 0).toLocaleString()}
                          </Text>
                        </div>
                        {Object.entries(o.tokens_by_channel || {}).map(([k, v]) => (
                          <div key={k} style={{ display: 'flex', alignItems: 'center', gap: 6, marginTop: 3, fontSize: 11 }}>
                            <span style={{ width: 70, color: '#666' }}>{CHANNEL_KEY_LABELS[k] || k}</span>
                            <div style={{ flex: 1, height: 6, background: '#f0f0f0', borderRadius: 3, overflow: 'hidden' }}>
                              <div style={{
                                width: `${Math.max((v / maxToken) * 100, 5)}%`, height: '100%',
                                background: 'linear-gradient(90deg, #722ed1, #eb2f96)',
                              }} />
                            </div>
                            <span style={{ width: 50, textAlign: 'right', color: '#722ed1' }}>{v.toLocaleString()}</span>
                          </div>
                        ))}
                      </div>

                      <Divider style={{ margin: '8px 0' }} />
                      <div style={{ display: 'flex', justifyContent: 'space-between', fontSize: 12, color: '#666' }}>
                        <span>✓ Done {o.tasks_completed}</span>
                        <span>⏳ Pending {o.tasks_pending}</span>
                      </div>
                    </Card>
                  </Col>
                );
              })}
            </Row>
          )}
          <style>{`@keyframes aiSalesLivePulse { 0%, 100% { transform: scale(1); opacity: 1; } 50% { transform: scale(1.2); opacity: 0.7; } }`}</style>
        </>
      ),
    },
    {
      key: 'leaderboard',
      label: <span><TrophyOutlined /> Leaderboard</span>,
      children: (
        <>
          <Row gutter={[12, 12]} style={{ marginBottom: 16 }} align="middle">
            <Col>
              <Text type="secondary" style={{ marginRight: 8 }}>結算期間</Text>
              <Select size="small" value={lbPeriod} onChange={setLbPeriod}
                options={PERIOD_OPTIONS} style={{ width: 110 }} />
            </Col>
            <Col>
              <Text type="secondary" style={{ marginRight: 8 }}>排名指標</Text>
              <Select size="small" value={lbMetric} onChange={setLbMetric}
                options={METRIC_OPTIONS} style={{ width: 140 }} />
            </Col>
            <Col>
              <Text type="secondary" style={{ marginRight: 8 }}><GlobalOutlined /> 國家</Text>
              <Select size="small" value={lbCountry} onChange={setLbCountry}
                allowClear placeholder="全部"
                options={COUNTRY_OPTIONS.map(c => ({
                  value: c.code,
                  label: `${c.flag} ${c.label}`,
                }))} style={{ width: 130 }} />
            </Col>
            <Col flex="auto" style={{ textAlign: 'right' }}>
              <Button size="small" icon={<ReloadOutlined />} onClick={fetchLeaderboard}>
                重新整理
              </Button>
            </Col>
          </Row>
          <Table columns={lbColumns} dataSource={leaderboard} rowKey="bot_id"
            loading={loading} size="middle" pagination={false} scroll={{ x: 1000 }} />
        </>
      ),
    },
    {
      key: 'competitions',
      label: <span><CrownOutlined /> Competitions</span>,
      children: (
        <>
          <div style={{ marginBottom: 16, textAlign: 'right' }}>
            <Button type="primary" icon={<PlusOutlined />}
              onClick={() => { setEditingCompId(null); compForm.resetFields(); setCompModal(true); }}>
              New Competition
            </Button>
          </div>
          <Table columns={compColumns} dataSource={competitions} rowKey="id"
            loading={loading} size="middle" pagination={{ pageSize: 10 }} scroll={{ x: 1000 }} />
        </>
      ),
    },
    {
      key: 'bots',
      label: <span><RobotOutlined /> AI Bots</span>,
      children: (
        <>
          <div style={{ marginBottom: 16, textAlign: 'right' }}>
            <Button type="primary" icon={<PlusOutlined />}
              onClick={() => { setEditingBotId(null); botForm.resetFields(); setBotModal(true); }}>
              New Bot
            </Button>
          </div>
          <Table columns={botColumns} dataSource={bots} rowKey="id"
            loading={loading} size="middle" pagination={{ pageSize: 10 }} scroll={{ x: 1100 }} />
        </>
      ),
    },
  ];

  return (
    <div>
      <Card style={{ marginBottom: 16 }} styles={{ body: { padding: 0 } }}>
        <div style={{
          background: 'linear-gradient(135deg, #667eea 0%, #764ba2 100%)',
          padding: '24px 32px', borderRadius: '8px 8px 0 0',
        }}>
          <Space align="center" size={16}>
            <TrophyOutlined style={{ fontSize: 32, color: '#fff' }} />
            <div>
              <Title level={3} style={{ margin: 0, color: '#fff' }}>AI Sales Competition</Title>
              <Text style={{ color: 'rgba(255,255,255,0.85)' }}>AI-powered sales bots with targets, leaderboard & rewards</Text>
            </div>
          </Space>
        </div>
      </Card>

      <Row gutter={[16, 16]} style={{ marginBottom: 16 }}>
        <Col xs={12} sm={6}>
          <Card><Statistic title="Total Bots" value={d.total_bots || 0} prefix={<RobotOutlined />} /></Card>
        </Col>
        <Col xs={12} sm={6}>
          <Card><Statistic title="Active Bots" value={d.active_bots || 0} valueStyle={{ color: '#52c41a' }} prefix={<TeamOutlined />} /></Card>
        </Col>
        <Col xs={12} sm={6}>
          <Card><Statistic title="Total Revenue" value={d.total_revenue || 0} prefix="NT$"
            valueStyle={{ color: '#1890ff' }} /></Card>
        </Col>
        <Col xs={12} sm={6}>
          <Card><Statistic title="ROI" value={d.roi || 0} suffix="%" prefix={<RiseOutlined />}
            valueStyle={{ color: (d.roi || 0) >= 0 ? '#52c41a' : '#ff4d4f' }} /></Card>
        </Col>
      </Row>

      <div style={{ marginBottom: 16 }}>
        <Button icon={<ReloadOutlined />} onClick={fetchAll}>Refresh</Button>
      </div>

      <Card>
        <Tabs activeKey={activeTab} onChange={setActiveTab} items={tabItems} />
      </Card>

      {/* Competition Modal */}
      <Modal title={editingCompId ? 'Edit Competition' : 'New Competition'} open={compModal}
        onOk={handleSaveComp} onCancel={() => { setCompModal(false); setEditingCompId(null); }}
        width={600} destroyOnClose>
        <Form form={compForm} layout="vertical" initialValues={{ period_type: 'monthly', status: 'draft' }}>
          <Form.Item name="name" label="Name" rules={[{ required: true }]}><Input /></Form.Item>
          <Row gutter={16}>
            <Col span={12}>
              <Form.Item name="period_type" label="Period Type" rules={[{ required: true }]}>
                <Select options={[
                  { value: 'monthly', label: 'Monthly' },
                  { value: 'quarterly', label: 'Quarterly' },
                  { value: 'annual', label: 'Annual' },
                ]} />
              </Form.Item>
            </Col>
            <Col span={12}>
              <Form.Item name="status" label="Status">
                <Select options={[
                  { value: 'draft', label: 'Draft' },
                  { value: 'active', label: 'Active' },
                  { value: 'paused', label: 'Paused' },
                  { value: 'completed', label: 'Completed' },
                ]} />
              </Form.Item>
            </Col>
          </Row>
          <Row gutter={16}>
            <Col span={12}>
              <Form.Item name="start_date" label="Start Date"><DatePicker style={{ width: '100%' }} /></Form.Item>
            </Col>
            <Col span={12}>
              <Form.Item name="end_date" label="End Date"><DatePicker style={{ width: '100%' }} /></Form.Item>
            </Col>
          </Row>
          <Form.Item name="target_amount" label="Target Amount (NT$)">
            <InputNumber min={0} style={{ width: '100%' }} />
          </Form.Item>
          <Form.Item name="reward_description" label="Reward Description">
            <TextArea rows={2} placeholder="e.g. 達標獎金 NT$10,000 + 額外假期" />
          </Form.Item>
          <Form.Item name="rules" label="Competition Rules">
            <TextArea rows={3} placeholder="e.g. 以實際成交金額計算，每月結算排名" />
          </Form.Item>
        </Form>
      </Modal>

      {/* Bot Modal */}
      <Modal title={editingBotId ? 'Edit Bot' : 'New Bot'} open={botModal}
        onOk={handleSaveBot} onCancel={() => { setBotModal(false); setEditingBotId(null); }}
        width={600} destroyOnClose>
        <Form form={botForm} layout="vertical" initialValues={{ status: 'active' }}>
          <Row gutter={16}>
            <Col span={16}>
              <Form.Item name="name" label="Bot Name" rules={[{ required: true }]}><Input placeholder="e.g. 業務小明" /></Form.Item>
            </Col>
            <Col span={8}>
              <Form.Item name="status" label="Status">
                <Select options={[
                  { value: 'active', label: 'Active' },
                  { value: 'paused', label: 'Paused' },
                  { value: 'retired', label: 'Retired' },
                ]} />
              </Form.Item>
            </Col>
          </Row>
          <Form.Item name="avatar_url" label="Avatar URL"><Input placeholder="https://..." /></Form.Item>
          <Form.Item name="personality" label="Personality">
            <TextArea rows={2} placeholder="e.g. 積極主動、善於溝通、專業友善" />
          </Form.Item>
          <Row gutter={16}>
            <Col span={12}>
              <Form.Item name="competition_id" label="Competition">
                <Select allowClear placeholder="Select competition"
                  options={competitions.map(c => ({ value: c.id, label: c.name }))} />
              </Form.Item>
            </Col>
            <Col span={12}>
              <Form.Item name="country_code" label={<span><GlobalOutlined /> 國家 / 地區</span>}
                tooltip="先支援台/泰/越/澳。需要其他國家時告訴我們，會加進預設清單">
                <Select
                  allowClear
                  showSearch
                  placeholder="選擇地區"
                  optionFilterProp="label"
                  options={COUNTRY_OPTIONS.map(c => ({
                    value: c.code,
                    label: `${c.flag} ${c.label} (${c.code})`,
                  }))}
                />
              </Form.Item>
            </Col>
          </Row>
          <Row gutter={16}>
            <Col span={12}>
              <Form.Item name="target_amount" label="Sales Target (NT$)">
                <InputNumber min={0} style={{ width: '100%' }} />
              </Form.Item>
            </Col>
          </Row>
          <Form.Item name="strategy_config" label="Sales Strategy">
            <TextArea rows={2} placeholder="e.g. 專注科技業客戶，每週發送 2 封推廣信" />
          </Form.Item>

          <Divider orientation="left" style={{ marginTop: 8 }}>
            <span style={{ fontSize: 13, color: '#475569' }}>
              <RobotOutlined /> AlayaEss Agent（Mode B 綁定，留空則用單一 Mika）
            </span>
          </Divider>
          <Form.Item
            name="alayaess_bot_name"
            label="AlayaEss 真實 agent"
            tooltip="從 AlayaEss admin 註冊的 bot 中挑一個，這個 AI Sales bot 之後產出的所有 AI 文案會用該 agent 的 persona / role_override。留空則 fallback 走 Mode A（共用 Mika）。"
            extra={<AlayaessAgentPickerHint />}
          >
            <AlayaessAgentPicker />
          </Form.Item>

          <Divider orientation="left" style={{ marginTop: 8 }}>
            <span style={{ fontSize: 13, color: '#475569' }}>
              <ThunderboltOutlined /> AI Token 配額（累計上限，永不歸零）
            </span>
          </Divider>
          <Row gutter={16}>
            <Col span={12}>
              <Form.Item
                name="public_token_limit"
                label={<span>Public 模型上限 <Typography.Text type="secondary" style={{ fontSize: 12 }}>(Claude + OpenAI)</Typography.Text></span>}
                tooltip="留空 = 不限。累計達 80% 會在 log 中警示，達 100% 該 bot 的 LLM 呼叫會被硬擋（429）。"
              >
                <InputNumber min={0} step={1000} placeholder="無上限" style={{ width: '100%' }} addonAfter="tok" />
              </Form.Item>
              <BotQuotaUsage form={botForm} editingBotId={editingBotId} bots={bots} kind="public" />
            </Col>
            <Col span={12}>
              <Form.Item
                name="private_token_limit"
                label={<span>Private 模型上限 <Typography.Text type="secondary" style={{ fontSize: 12 }}>(OLLAMA + 本地)</Typography.Text></span>}
                tooltip="留空 = 不限。"
              >
                <InputNumber min={0} step={1000} placeholder="無上限" style={{ width: '100%' }} addonAfter="tok" />
              </Form.Item>
              <BotQuotaUsage form={botForm} editingBotId={editingBotId} bots={bots} kind="private" />
            </Col>
          </Row>

          <Divider orientation="left" style={{ marginTop: 8 }}>
            <span style={{ fontSize: 13, color: '#475569' }}>
              🤖 自主性 + 安全闌（管理員可隨時切換模式）
            </span>
          </Divider>

          <Form.Item
            name="autonomy_mode"
            label="自主性模式"
            tooltip="決定這個 bot 多主動朝業績努力。可以隨時切換。"
            initialValue="manual"
          >
            <Select
              options={[
                { value: 'manual', label: 'Manual — 完全人工觸發（預設）' },
                { value: 'inbox', label: 'Inbox — 每 30 分鐘產生建議到審核佇列，人類核准才動' },
                { value: 'daily_plan', label: 'Daily Plan — 每日清晨排當日計畫，分時段自動觸發（需核准的不自動）' },
                { value: 'loop', label: 'Loop — 全自主，每 30 分鐘自己挑工具自己做（注意 token 燒燃速度）' },
              ]}
            />
          </Form.Item>

          <Row gutter={8}>
            <Col span={8}>
              <Form.Item name="daily_limit_fb_post" label="FB 每日上限" initialValue={3}>
                <InputNumber min={0} max={50} style={{ width: '100%' }} />
              </Form.Item>
            </Col>
            <Col span={8}>
              <Form.Item name="daily_limit_email_blast" label="Email 每日上限" initialValue={5}>
                <InputNumber min={0} max={50} style={{ width: '100%' }} />
              </Form.Item>
            </Col>
            <Col span={8}>
              <Form.Item name="daily_limit_blog_post" label="Blog 每日上限" initialValue={1}>
                <InputNumber min={0} max={10} style={{ width: '100%' }} />
              </Form.Item>
            </Col>
            <Col span={8}>
              <Form.Item name="daily_limit_video_generate" label="Video 每日上限" initialValue={0}>
                <InputNumber min={0} max={10} style={{ width: '100%' }} />
              </Form.Item>
            </Col>
            <Col span={8}>
              <Form.Item name="daily_limit_google_ads_create" label="Google Ads 每日上限" initialValue={1}>
                <InputNumber min={0} max={10} style={{ width: '100%' }} />
              </Form.Item>
            </Col>
            <Col span={8}>
              <Form.Item
                name="weekly_budget_usd"
                label="每週預算 (USD)"
                tooltip="廣告 + 任何有成本的動作合計不可超過此值"
                initialValue={100}
              >
                <InputNumber min={0} step={50} style={{ width: '100%' }} addonBefore="$" />
              </Form.Item>
            </Col>
          </Row>

          <Form.Item
            name="require_approval_actions"
            label="需要人工核准才會發送的動作"
            tooltip="勾選的動作會先生成 proposal 進審核佇列，等你按「核准」才真的執行。FB 與 Email 預設都要核准（blast-radius 大）。"
            initialValue={['fb_post', 'email_blast']}
          >
            <Select
              mode="multiple"
              allowClear
              options={[
                { value: 'fb_post', label: '📘 FB 貼文' },
                { value: 'email_blast', label: '📧 Email 群發' },
                { value: 'blog_post', label: '📰 Blog 文章' },
                { value: 'google_ads_create', label: '💰 Google Ads' },
                { value: 'video_generate', label: '🎬 Video' },
              ]}
            />
          </Form.Item>
        </Form>
      </Modal>

      {/* Activities Modal */}
      <Modal title="Bot Activities" open={!!activitiesModal}
        onCancel={() => setActivitiesModal(null)} footer={null} width={700}>
        {activities.length === 0 ? <Text type="secondary">No activities yet</Text> : (
          <List dataSource={activities} renderItem={item => (
            <List.Item>
              <List.Item.Meta
                avatar={<Avatar icon={
                  item.activity_type === 'email_blast' ? <MailOutlined /> :
                  item.activity_type === 'fb_post' ? <FacebookOutlined /> :
                  <FileTextOutlined />
                } style={{
                  background: item.result?.ok ? '#52c41a' : '#ff4d4f'
                }} />}
                title={
                  <Space>
                    <Tag>{item.activity_type}</Tag>
                    <Tag color={item.channel === 'email' ? 'blue' : item.channel === 'facebook' ? 'geekblue' : 'default'}>
                      {item.channel}
                    </Tag>
                    {item.revenue_amount > 0 && <Tag color="green">+NT${item.revenue_amount.toLocaleString()}</Tag>}
                  </Space>
                }
                description={
                  <div>
                    <div>{item.content_summary}</div>
                    <Text type="secondary" style={{ fontSize: 11 }}>
                      {dayjs(item.created_at).format('YYYY-MM-DD HH:mm')}
                      {item.cost_amount > 0 && ` | Cost: NT$${item.cost_amount.toLocaleString()}`}
                    </Text>
                  </div>
                }
              />
            </List.Item>
          )} />
        )}
      </Modal>

      {/* Action Modal */}
      <Modal title="Execute Bot Action" open={!!actionModal}
        onCancel={() => setActionModal(null)} footer={null} width={480}>
        <Space direction="vertical" style={{ width: '100%' }} size="middle">
          <Card hoverable onClick={() => handleBotAction(actionModal, 'email_blast')}
            style={{ cursor: 'pointer' }}>
            <Space>
              <Avatar icon={<MailOutlined />} style={{ background: '#1890ff' }} />
              <div>
                <Text strong>Email Blast</Text>
                <div><Text type="secondary">AI generates promotional email and sends to contacts</Text></div>
              </div>
            </Space>
          </Card>
          <Card hoverable onClick={() => handleBotAction(actionModal, 'fb_post')}
            style={{ cursor: 'pointer' }}>
            <Space>
              <Avatar icon={<FacebookOutlined />} style={{ background: '#4267B2' }} />
              <div>
                <Text strong>Facebook Post</Text>
                <div><Text type="secondary">AI creates and publishes a promotional post</Text></div>
              </div>
            </Space>
          </Card>
          <Card hoverable onClick={() => handleBotAction(actionModal, 'generate_content')}
            style={{ cursor: 'pointer' }}>
            <Space>
              <Avatar icon={<FileTextOutlined />} style={{ background: '#52c41a' }} />
              <div>
                <Text strong>Generate Content</Text>
                <div><Text type="secondary">AI writes a marketing article (draft only, not published)</Text></div>
              </div>
            </Space>
          </Card>
          <Card hoverable onClick={() => handleBotAction(actionModal, 'blog_post')}
            style={{ cursor: 'pointer' }}>
            <Space>
              <Avatar icon={<ReadOutlined />} style={{ background: '#fa8c16' }} />
              <div>
                <Text strong>Blog Post</Text>
                <div><Text type="secondary">AI generates SEO blog article & creates draft in /blog</Text></div>
              </div>
            </Space>
          </Card>
          <Card hoverable onClick={() => handleBotAction(actionModal, 'video_generate')}
            style={{ cursor: 'pointer' }}>
            <Space>
              <Avatar icon={<VideoCameraOutlined />} style={{ background: '#eb2f96' }} />
              <div>
                <Text strong>Video Generate (Wav2Lip)</Text>
                <div><Text type="secondary">Queue AI-narrated product video — finalize in /wav2lip</Text></div>
              </div>
            </Space>
          </Card>
          <Card hoverable onClick={() => handleBotAction(actionModal, 'google_ads_create')}
            style={{ cursor: 'pointer' }}>
            <Space>
              <Avatar icon={<GoogleOutlined />} style={{ background: '#4285F4' }} />
              <div>
                <Text strong>Google Ads</Text>
                <div><Text type="secondary">AI drafts search ad copy & estimates budget — push in /google-ads</Text></div>
              </div>
            </Space>
          </Card>
        </Space>
      </Modal>
    </div>
  );
}
